import { detailedDiff, diff } from 'deep-object-diff';
import produce from 'immer';
import { groupBy, isEmpty, isEqual, keyBy, merge, snakeCase } from 'lodash/fp';
import qs from 'qs';
import { batch } from 'react-redux';
import { NavigateFunction } from 'react-router-dom-v5-compat';
import { getFormValues } from 'redux-form';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { getWorkflowSchemaLiveUpdated } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/selectors/form';
import type {
  AssetUploadData,
  InquiryTemplateEditorFormData,
  InquiryTemplateUpdateData,
  InquiryTemplateVersionUpdateData,
  LoadValidationErrorsFormData,
  ScreenDimensionMap,
  ScreenDimensions,
  UploadedAsset,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  clampScreenDimensions,
  screenDimensionsExceedMaxScreenDimensions,
} from 'dashboard/apps/InquiryTemplateEditor/utils/screen-dimensions';
import { LegacyTemplateConfigFormData } from 'dashboard/forms/FormLegacyTemplate';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { InquiryTemplateVersionError } from 'dashboard/lib/interfaces/InquiryTemplateVersionErrors';
import { createMockDirectTrigger } from 'dashboard/lib/stubs/trigger';
import { createInputStep } from 'dashboard/lib/workflows';
import { getSchemaId } from 'dashboard/lib/workflows/platform-object';
import { makeStepsConfigUIReady } from 'dashboard/lib/workflows/steps-config-operations';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  BaseInquiryTemplateVersionData,
  InquiryTemplateVersion,
  InquiryTemplateVersionDiff,
  InquiryTemplateVersionSchema,
  InquiryTemplateVersionStatus,
} from 'dashboard/models/inquiry-template-version';
import { Template } from 'dashboard/models/template';
import { ThemeSet } from 'dashboard/models/theme-set';
import {
  WorkflowFieldKey,
  WorkflowFieldObjectKey,
  WorkflowSchema,
} from 'dashboard/models/workflow';
import { setFormValues } from 'dashboard/redux/actions/inquiry-template';
import { setInputStep, setStepContexts } from 'dashboard/redux/actions/workflow';
import { workflowSchemaEndpoints } from 'dashboard/redux/api/workflow-schema';
import { getIsPlatformObjectJsonSchemaOnFLEEnabled } from 'dashboard/redux/selectors/fle-workflow-context';
import {
  setAssets,
  setComparedSchema,
  setComparedVersion,
  setComponentPreview,
  setEditorHistory,
  setEditorScreenDimensions,
  setInquiryTemplate,
  setInquiryTemplateTreeData,
  setLocale,
  setMaxEditorScreenDimensions,
  setStepLocalizedStrings,
  setStepsPreview,
  setValidationErrors,
  setVersionDiff,
  StepLocalizedStrings,
} from 'dashboard/redux/slices/inquiry-template';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import {
  loadPartialInquiryTemplateVersions,
  removeLocalDraftVersion,
} from 'dashboard/redux/thunk-actions/inquiry-templates';
import { loadNewAndLegacyInquiryTemplateList } from 'dashboard/redux/thunk-actions/organization';
import { loadWorkflowSchemaParallel } from 'dashboard/redux/thunk-actions/workflow-schema';
import { DashboardThunkAction } from 'dashboard/redux/types';
import StorageClient, { LocalStorageKey } from 'lib/clients/StorageClient';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { convertFieldSchemasToJsonSchema } from 'lib/fields';
import { ApiEndpoint, ApiModel, ApiPayload, ApiResponse } from 'lib/interfaces/api';
import type { StepPreview } from 'lib/interfaces/StepsConfig';
import { UiComponent, UiComponentConfig } from 'lib/interfaces/ui-components';
import { JsonSchemaTypes } from 'lib/json-schema';
import { readImageAspectRatio } from 'lib/utils/image';

const BASE_PATH = '/dashboard/inquiry-templates';

export const getEditorPath = (templateId: string, versionId: string, path: string): string =>
  `${BASE_PATH}/${templateId}/${versionId}/${path}`;

/**
 * 2.0 Template thunk actions
 */
export const loadLegacyTemplate =
  (templateId: string): DashboardThunkAction =>
  async (dispatch) => {
    const { data: template } = await SessionClientDashboard.getResource<Template>(
      ApiEndpoint.Template,
      templateId,
    );
    dispatch(setInquiryTemplate({ inquiryTemplate: template }));
  };

export const updateLegacyTemplate =
  (formData: LegacyTemplateConfigFormData, templateId: string): DashboardThunkAction =>
  async (dispatch) => {
    const { data: newTemplate } = await SessionClientDashboard.updateResource<Template>({
      id: templateId,
      type: ApiModel.Template,
      attributes: formData,
    });
    dispatch(setInquiryTemplate({ inquiryTemplate: newTemplate }));
  };

/**
 * Inquiry Template thunk actions
 */
export const createInquiryTemplate =
  (
    inquiryTemplateBuilderName: string,
  ): DashboardThunkAction<
    Promise<{ inquiryTemplate: InquiryTemplate; inquiryTemplateVersion: InquiryTemplateVersion }>
  > =>
  async (dispatch) => {
    const { data: inquiryTemplate, included } = await SessionClientDashboard.postResourceCreate<
      InquiryTemplate,
      {
        type: ApiModel.InquiryTemplate;
        attributes: { builder: string };
      }
    >({
      type: ApiModel.InquiryTemplate,
      attributes: {
        builder: inquiryTemplateBuilderName,
      },
    });
    const inquiryTemplateVersion = included!.find(
      (include) => include.type === ApiModel.InquiryTemplateVersion,
    ) as InquiryTemplateVersion;

    // Bust cached organization-level itmpl list
    // Not blocking and failable without issue -- just results in minorly stale data
    try {
      dispatch(loadNewAndLegacyInquiryTemplateList());
    } catch {
      // noop
    }

    return {
      inquiryTemplate,
      inquiryTemplateVersion,
    };
  };

export const cloneInquiryTemplate =
  (
    inquiryTemplateId: string,
    name: string | undefined,
  ): DashboardThunkAction<
    Promise<{ inquiryTemplate: InquiryTemplate; inquiryTemplateVersion: InquiryTemplateVersion }>
  > =>
  async (dispatch) => {
    const { data: inquiryTemplate, included } = await SessionClientDashboard.postResourceAction<
      InquiryTemplate,
      {
        type: ApiModel.InquiryTemplate;
        attributes: { name: string | undefined };
        id: string;
      }
    >(
      {
        type: ApiModel.InquiryTemplate,
        attributes: {
          name: name,
        },
        id: inquiryTemplateId,
      },
      'clone',
    );
    const inquiryTemplateVersion = included!.find(
      (include) => include.type === ApiModel.InquiryTemplateVersion,
    ) as InquiryTemplateVersion;

    // Bust cached organization-level itmpl list
    // Not blocking and failable without issue -- just results in minorly stale data
    try {
      dispatch(loadNewAndLegacyInquiryTemplateList());
    } catch {
      // noop
    }

    return {
      inquiryTemplate,
      inquiryTemplateVersion,
    };
  };

export const updateInquiryTemplate =
  (inquiryTemplateId: string, formData: InquiryTemplateUpdateData): DashboardThunkAction =>
  async (dispatch) => {
    const { data: newTemplate } = await SessionClientDashboard.updateResource<InquiryTemplate>({
      id: inquiryTemplateId,
      type: ApiModel.InquiryTemplate,
      attributes: formData,
    });
    dispatch(setInquiryTemplate({ inquiryTemplate: newTemplate }));
  };

/**
 * Main loader used when initially loading the editor or
 * when the user selects a different version to view
 */
export const loadInquiryTemplateVersion =
  (inquiryTemplateVersionId: string): DashboardThunkAction =>
  async (dispatch, getState) => {
    const state = getState();
    const currentLocale = state.inquiryTemplate?.locale;
    const [{ data: inquiryTemplateVersion, included }, stepsPreview, schema] = await Promise.all([
      SessionClientDashboard.getResource<InquiryTemplateVersion>(
        ApiEndpoint.InquiryTemplateVersion,
        inquiryTemplateVersionId,
      ),
      // client data (preview) - locale defaults to first enabled-locale on the server if en-US is not included
      fetchStepsPreview(inquiryTemplateVersionId, currentLocale ?? 'en-US'),
      // related objects (wfv, vtv)
      fetchSchema(inquiryTemplateVersionId),
    ]);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const inquiryTemplate = included.find(
      (entity) => entity.type === ApiModel.InquiryTemplate,
    ) as InquiryTemplate;

    dispatch(
      setInquiryTemplateVersionData({
        inquiryTemplateVersion,
        inquiryTemplate,
        schema,
        stepsPreview,
      }),
    );
    dispatch(loadRelatedITVData(inquiryTemplateVersionId));
  };

export const initializeWorkflowStepContexts = (inquiryTemplateId: string): DashboardThunkAction => {
  return async (dispatch, getState) => {
    const enablePlatformObjectJsonSchema = getIsPlatformObjectJsonSchemaOnFLEEnabled(getState());
    await dispatch(
      loadWorkflowSchemaParallel({ loadCriteriaVariableSchemas: !enablePlatformObjectJsonSchema }),
    );
    const inputStep = createInputStep({
      trigger: createMockDirectTrigger(),
      filter: { type: ApiModel.Inquiry, id: inquiryTemplateId },
    });
    dispatch(setInputStep(inputStep));
    await dispatch(generateAllWorkflowStepContexts({ inquiryTemplateId }));
  };
};

export const generateAllWorkflowStepContexts = ({
  inquiryTemplateId,
  workflowSchemaOverride,
  versionsMapOverride,
}: {
  inquiryTemplateId: string;
  workflowSchemaOverride?: WorkflowSchema;
  versionsMapOverride?: WorkflowVersionMap;
}): DashboardThunkAction => {
  return async (dispatch, getState) => {
    const rootState = getState();
    const enablePlatformObjectJsonSchema = getIsPlatformObjectJsonSchemaOnFLEEnabled(rootState);
    const inputStep = rootState.workflow.present.inputStep;
    const workflowSchema = workflowSchemaOverride || getWorkflowSchemaLiveUpdated(rootState);
    const form = getFormValues(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(
      rootState,
    ) as InquiryTemplateEditorFormData;
    const versionsMap = versionsMapOverride || (form?.workflowVersions ?? {});
    const objectSchemas = enablePlatformObjectJsonSchema
      ? await dispatch(
          workflowSchemaEndpoints.getBaseWorkflowObjectJsonSchemas.initiate([
            ...(inputStep.filter ? [inputStep.filter] : []),
            ...Object.values(versionsMap).flatMap(
              (version) => version.attributes['template-filters'],
            ),
          ]),
        ).unwrap()
      : undefined;
    const inquiryTemplateFieldsJsonSchema: ObjectSchemaMap = {
      [getSchemaId(WorkflowFieldObjectKey.Inquiry, inquiryTemplateId)]: {
        type: JsonSchemaTypes.Object,
        properties: {
          [WorkflowFieldKey.Fields]: convertFieldSchemasToJsonSchema(form?.['field-schema'] || []),
        },
      },
    };
    // (evan.qi) fields can be added to the inquiry template on the flow editor, but not yet
    // saved onto the published version. we still want these fields to be available for use
    // prior to the version being published, so we merge the field schemas into the schema
    // for the inquiry template that was fetched.
    const platformObjectJsonSchema = objectSchemas
      ? merge(objectSchemas, inquiryTemplateFieldsJsonSchema)
      : undefined;
    const combinedStepContexts = Object.values(versionsMap).reduce((combined, version) => {
      const { stepContexts } = makeStepsConfigUIReady({
        steps: version.attributes['steps-config'],
        inputStep,
        workflowSchema,
        platformObjectJsonSchema,
      });
      return {
        ...combined,
        ...stepContexts,
      };
    }, {});
    dispatch(setStepContexts(combinedStepContexts));
  };
};

/**
 * Helper loading function the template update actions - save, discard, publish.
 * The main loader calls the ITV/stepsPreview/schema loaders in parallel to unblock
 * the UI faster since it doesn't need to wait on the outcome of the ITV action.
 *
 * All of the loaders for data directly related to the template
 * itself are consolidated here to ensure that the editor always
 * has the most up-to-date data following an editor action.
 */
const loadInquiryTemplateVersionData =
  (
    inquiryTemplateVersion: InquiryTemplateVersion,
    inquiryTemplate: InquiryTemplate,
    omitStepsPreviewUpdate: boolean,
  ): DashboardThunkAction =>
  async (dispatch, getState) => {
    const { id: versionId } = inquiryTemplateVersion;
    // defaults to first enabled-locale on the server if en-US is not included
    const currentLocale = getState().inquiryTemplate?.locale ?? 'en-US';
    const [stepsPreview, schema] = await Promise.all([
      // client data (preview)
      omitStepsPreviewUpdate ? Promise.resolve([]) : fetchStepsPreview(versionId, currentLocale),
      // related objects (wfv, vtv)
      fetchSchema(versionId),
    ]);
    dispatch(
      setInquiryTemplateVersionData({
        inquiryTemplateVersion,
        inquiryTemplate,
        schema,
        stepsPreview,
        omitStepsPreviewUpdate,
      }),
    );
    dispatch(loadRelatedITVData(versionId));
    // Load list of all versions for version history panel
    dispatch(loadPartialInquiryTemplateVersions(inquiryTemplate.id));
  };

const setInquiryTemplateVersionData =
  ({
    inquiryTemplateVersion,
    inquiryTemplate,
    schema,
    stepsPreview,
    omitStepsPreviewUpdate = false,
  }: {
    inquiryTemplateVersion: InquiryTemplateVersion;
    inquiryTemplate: InquiryTemplate;
    schema: InquiryTemplateVersionSchema;
    stepsPreview: StepPreview[];
    omitStepsPreviewUpdate?: boolean;
  }): DashboardThunkAction =>
  (dispatch, getState) => {
    const currentLocale = getState().inquiryTemplate?.locale;
    const { 'enabled-locales': enabledLocales } = inquiryTemplateVersion.attributes;
    batch(() => {
      dispatch(
        setInquiryTemplateTreeData({
          inquiryTemplateVersion,
          schema: {
            workflowVersions: keyBy('id', schema['workflow-versions'].data),
            verificationTemplateVersions: keyBy(
              'id',
              schema['verification-template-versions'].data,
            ),
            matchRequirementProfiles: groupBy(
              'object-id',
              schema['match-requirement-profiles'].data,
            ),
          },
        }),
      );
      if (!omitStepsPreviewUpdate) {
        dispatch(setStepsPreview({ stepsPreview }));
      }
      dispatch(setInquiryTemplate({ inquiryTemplate }));
      dispatch(
        setLocale({
          locale: enabledLocales.includes(currentLocale) ? currentLocale : enabledLocales[0],
        }),
      );
    });
  };

/**
 * Separate loader for stepsPreview (client data), as the template preview can be reloaded
 * in a different locale from the screen and theme editors. The newly fetched preview is set
 * in redux with the same prior inquiryTemplateVersion and schema data.
 */
export const loadStepsPreview =
  (inquiryTemplateVersionId: string, locale: InquiryIso6391LanguageCode): DashboardThunkAction =>
  async (dispatch, getState) => {
    const form = getFormValues(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(
      getState(),
    ) as InquiryTemplateEditorFormData;

    const stepsPreview = await fetchStepsPreview(inquiryTemplateVersionId, locale, {
      noITVThemeSet: true,
      themeSetId: form.themeSetId,
    });
    dispatch(setLocale({ locale }));
    dispatch(setStepsPreview({ stepsPreview }));
  };

interface ThemeSetLoadingConfig {
  noITVThemeSet: boolean;
  themeSetId?: string;
}

export async function fetchStepsPreview(
  inquiryTemplateVersionId: string,
  locale: InquiryIso6391LanguageCode,
  themeSetLoadingConfig?: ThemeSetLoadingConfig,
): Promise<StepPreview[]> {
  const optionsPayload = themeSetLoadingConfig
    ? {
        locale,
        'no-itv-theme': themeSetLoadingConfig.noITVThemeSet,
        'theme-set-id': themeSetLoadingConfig.themeSetId,
      }
    : { locale };
  const {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    meta: { steps: stepsPreview },
  } = await SessionClientDashboard.getResource<InquiryTemplateVersion, { steps: StepPreview[] }>(
    ApiEndpoint.InquiryTemplateVersion,
    `${inquiryTemplateVersionId}/steps-as-client-data?${qs.stringify(optionsPayload)}`,
  );
  return stepsPreview;
}

export async function fetchPartialStepsPreview(
  inquiryTemplateVersionId: string,
  locale: InquiryIso6391LanguageCode,
  themeSet: ThemeSet,
  startPaginationIndex: number,
  endPaginationIndex: number,
): Promise<StepPreview[]> {
  const optionsPayload = {
    locale,
    'theme-set': btoa(JSON.stringify(themeSet)),
    range: { start: startPaginationIndex, end: endPaginationIndex },
  };

  const { meta } = await SessionClientDashboard.getResource<
    InquiryTemplateVersion,
    { steps: StepPreview[] }
  >(
    ApiEndpoint.InquiryTemplateVersion,
    `${inquiryTemplateVersionId}/steps-as-client-data?${qs.stringify(optionsPayload)}`,
  );

  return meta?.steps || [];
}

export async function fetchComponentPreview(
  inquiryTemplateVersionId: string,
  stepName: string,
  componentConfig: UiComponentConfig,
  locale: InquiryIso6391LanguageCode,
  themeSetLoadingConfig?: ThemeSetLoadingConfig,
): Promise<UiComponent> {
  const {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    meta: { 'component-preview': componentPreview },
  } = await SessionClientDashboard.postResourceAction<
    InquiryTemplateVersion,
    {
      attributes: {
        'step-name': string;
        'component-config': UiComponentConfig;
        locale: InquiryIso6391LanguageCode;
        'no-itv-theme': boolean | undefined;
        'theme-set-id': string | undefined;
      };
      type: ApiModel.InquiryTemplateVersion;
      id: string;
    }
  >(
    {
      attributes: {
        'step-name': stepName,
        'component-config': componentConfig,
        locale,
        'no-itv-theme': themeSetLoadingConfig?.noITVThemeSet,
        'theme-set-id': themeSetLoadingConfig?.themeSetId,
      },
      type: ApiModel.InquiryTemplateVersion,
      id: inquiryTemplateVersionId,
    },
    'component-config-as-client-data',
  );

  return componentPreview;
}

export const refreshComponentPreview =
  (
    stepName: string,
    componentPath: string,
    componentConfig: UiComponentConfig,
  ): DashboardThunkAction =>
  async (dispatch, getState) => {
    const form = getFormValues(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(
      getState(),
    ) as InquiryTemplateEditorFormData;
    const { inquiryTemplateTreeData, locale } = getState().inquiryTemplate;
    const inquiryTemplateVersionId = inquiryTemplateTreeData.inquiryTemplateVersion.id;
    const componentPreview = await fetchComponentPreview(
      inquiryTemplateVersionId,
      stepName,
      componentConfig,
      locale ?? 'en-US',
      { noITVThemeSet: true, themeSetId: form.themeSetId },
    );
    dispatch(setComponentPreview({ componentPreview, componentPath, stepName }));
  };

async function fetchSchema(
  inquiryTemplateVersionId: string,
): Promise<InquiryTemplateVersionSchema> {
  const { meta: schema } = await SessionClientDashboard.getResource<
    InquiryTemplateVersion,
    InquiryTemplateVersionSchema
  >(ApiEndpoint.InquiryTemplateVersion, `${inquiryTemplateVersionId}/schema`);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return schema;
}

async function setAssetAspectRatios(assets: UploadedAsset[]): Promise<UploadedAsset[]> {
  // svg and lottie images don't have defined width and heights
  const imageContentTypes = ['image/webp', 'image/jpeg', 'image/png', 'image/gif'];

  const assetsWithAspectRatio = assets.map(async (asset) => {
    return {
      ...asset,
      'aspect-ratio': imageContentTypes.includes(asset['content-type'])
        ? await readImageAspectRatio(asset.url)
        : null,
    };
  });
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return Promise.all(assetsWithAspectRatio);
}

/**
 * Helper which should contain all of the loaders for dynamic (e.g not modules)
 * secondary data related to the template. Always called as non-blocking last step for ITV loader.
 */
const loadRelatedITVData =
  (inquiryTemplateVersionId: string): DashboardThunkAction =>
  (dispatch) => {
    // user-uploaded assets (currently per-template)
    dispatch(loadAssets(inquiryTemplateVersionId));
    // clear any local version data restored from the
    // version panel when loading a targeted version's data
    dispatch(removeLocalDraftVersion());
    // list of validation errors
    dispatch(loadValidationErrors(inquiryTemplateVersionId));
  };

export const loadAssets =
  (inquiryTemplateVersionId: string): DashboardThunkAction =>
  async (dispatch) => {
    const {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      meta: { assets },
    } = await SessionClientDashboard.getResource<
      InquiryTemplateVersion,
      { assets: UploadedAsset[] }
    >(ApiEndpoint.InquiryTemplateVersion, `${inquiryTemplateVersionId}/assets`);

    const assetsWithAspectRatio = await setAssetAspectRatios(assets);

    dispatch(setAssets({ assets: assetsWithAspectRatio }));
  };

export const loadValidationErrors =
  (
    inquiryTemplateVersionId: string,
    formData?: LoadValidationErrorsFormData,
  ): DashboardThunkAction =>
  async (dispatch) => {
    const { meta } = (await SessionClientDashboard.postResourceAction<
      InquiryTemplateVersion,
      ApiPayload<Partial<LoadValidationErrorsFormData>, ApiModel.InquiryTemplateVersion>
    >(
      {
        id: inquiryTemplateVersionId,
        type: ApiModel.InquiryTemplateVersion,
        attributes: {
          ...formData,
        },
      },
      'validation-errors',
    )) as unknown as { meta: { 'validation-errors': InquiryTemplateVersionError[] } };

    const validationErrors = meta?.['validation-errors'] || [];

    dispatch(setValidationErrors({ validationErrors }));
  };

export const saveInquiryTemplateVersion =
  (
    inquiryTemplateId: string,
    inquiryTemplateVersion: InquiryTemplateVersion,
    formData: InquiryTemplateVersionUpdateData,
    lockVersion: number,
    reloadPath: string,
    omitStepsPreviewUpdate: boolean,
    navigate: NavigateFunction,
  ): DashboardThunkAction =>
  async (dispatch, getState) => {
    // Create new draft if we have restored local draft or if we edited a published version
    const createNew =
      inquiryTemplateVersion.id == null ||
      inquiryTemplateVersion.attributes.status === InquiryTemplateVersionStatus.Published;

    let createdVersion = null;
    let createdVersionTemplate = null;
    let versionIdToSave: string;

    if (createNew) {
      const createVersionResponse = await createVersion(inquiryTemplateId);
      if (Array.isArray(createVersionResponse)) {
        [createdVersionTemplate, createdVersion] = createVersionResponse;
        versionIdToSave = createdVersion.id;
        // Need the lock version of the newly created ITV
        lockVersion = createdVersion.attributes['lock-version'];
      } else {
        versionIdToSave = createVersionResponse.id;
        // Need the lock version of the newly created ITV
        lockVersion = createVersionResponse['lock-version'];
      }
    } else {
      versionIdToSave = inquiryTemplateVersion.id;
    }

    try {
      const [inquiryTemplate, updatedVersion] = await saveVersion(
        versionIdToSave,
        formData,
        lockVersion,
      );
      await dispatch(
        loadInquiryTemplateVersionData(updatedVersion, inquiryTemplate, omitStepsPreviewUpdate),
      );
      // replace path if new draft created or if current path requires reloading client data (theme or screen editor)
      // old urls may refer to the wrong tokens so should be overwritten in the history stack
      navigate(getEditorPath(inquiryTemplateId, versionIdToSave, reloadPath), { replace: true });
    } catch (error) {
      if (createNew) {
        const { schema } = getState().inquiryTemplate.inquiryTemplateTreeData;
        if (createdVersion == null) {
          [createdVersionTemplate, createdVersion] = await fetchVersion(versionIdToSave);
        }
        dispatch(setInquiryTemplateTreeData({ inquiryTemplateVersion: createdVersion, schema }));
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        dispatch(setInquiryTemplate({ inquiryTemplate: createdVersionTemplate }));
        navigate(getEditorPath(inquiryTemplateId, versionIdToSave, reloadPath), { replace: true });
      }
      throw error;
    }
  };

async function fetchVersion(
  inquiryTemplateVersionId: string,
): Promise<[InquiryTemplate | undefined, InquiryTemplateVersion]> {
  const response = await SessionClientDashboard.getResource<InquiryTemplateVersion>(
    ApiEndpoint.InquiryTemplateVersion,
    inquiryTemplateVersionId,
  );

  const { data: createdVersion, included } = response;
  const inquiryTemplate = included?.find(
    (include): include is InquiryTemplate => include.type === ApiModel.InquiryTemplate,
  );

  return [inquiryTemplate, createdVersion];
}

interface InquiryTemplateVersionLightAttributes {
  id: string;
  'lock-version': number;
}

async function createVersion(
  inquiryTemplateId: string,
): Promise<[InquiryTemplate, InquiryTemplateVersion] | InquiryTemplateVersionLightAttributes> {
  const response = (await SessionClientDashboard.postResourceCreate<
    InquiryTemplateVersion,
    {
      type: ApiModel.InquiryTemplateVersion;
      attributes: { 'inquiry-template-id': string };
    }
  >({
    type: ApiModel.InquiryTemplateVersion,
    attributes: {
      'inquiry-template-id': inquiryTemplateId,
    },
  })) as ApiResponse<InquiryTemplateVersion, object> | InquiryTemplateVersionLightAttributes;

  if ('lock-version' in response) {
    return response;
  }

  const { data: createdVersion, included } = response;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const inquiryTemplate = included.find(
    (include): include is InquiryTemplate => include.type === ApiModel.InquiryTemplate,
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return [inquiryTemplate, createdVersion];
}

async function saveVersion(
  inquiryTemplateVersionId: string,
  formData: InquiryTemplateVersionUpdateData,
  lockVersion: number,
): Promise<[InquiryTemplate, InquiryTemplateVersion]> {
  const { data: updatedVersion, included } = await SessionClientDashboard.updateResource<
    InquiryTemplateVersion,
    ApiPayload<
      InquiryTemplateVersionUpdateData & { 'lock-version': number },
      ApiModel.InquiryTemplateVersion
    >
  >({
    id: inquiryTemplateVersionId,
    type: ApiModel.InquiryTemplateVersion,
    attributes: {
      ...formData,
      'lock-version': lockVersion,
    },
  });

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const inquiryTemplate = included.find(
    (include): include is InquiryTemplate => include.type === ApiModel.InquiryTemplate,
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return [inquiryTemplate, updatedVersion];
}

export const discardDraftInquiryTemplateVersion =
  ({
    inquiryTemplateId,
    reloadCurrentVersion = true,
    navigate,
  }: {
    inquiryTemplateId: string;
    reloadCurrentVersion?: boolean;
    navigate: NavigateFunction;
  }): DashboardThunkAction =>
  async (dispatch) => {
    const { data: inquiryTemplate, included } =
      await SessionClientDashboard.deleteResource<InquiryTemplate>(
        ApiEndpoint.InquiryTemplate,
        inquiryTemplateId,
      );
    // it's possible to discard versions using the version list panel while
    // viewing an older published version which is not the latest-published version.
    // In that scenario, there's no need to reload the template data.
    if (reloadCurrentVersion) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const publishedVersion = included.find(
        (include) => include.type === ApiModel.InquiryTemplateVersion,
      ) as InquiryTemplateVersion;
      await dispatch(loadInquiryTemplateVersionData(publishedVersion, inquiryTemplate, false));
      navigate(getEditorPath(inquiryTemplate.id, publishedVersion.id, 'flow'), { replace: true });
    } else {
      dispatch(loadPartialInquiryTemplateVersions(inquiryTemplate.id));
    }
  };

export const publishInquiryTemplateVersion =
  (
    inquiryTemplateVersionId: string,
    shouldUpdateEnhancedBilling: boolean,
    lockVersion: number,
  ): DashboardThunkAction =>
  async (dispatch) => {
    const { data: publishedVersion, included } =
      await SessionClientDashboard.postResourceAction<InquiryTemplateVersion>(
        {
          type: ApiModel.InquiryTemplateVersion,
          id: inquiryTemplateVersionId,
        },
        'publish',
        {
          meta: {
            'should-update-enhanced-billing': shouldUpdateEnhancedBilling,
            'lock-version': lockVersion,
          },
        },
      );
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const inquiryTemplate = included.find(
      (include) => include.type === ApiModel.InquiryTemplate,
    ) as InquiryTemplate;
    await dispatch(loadInquiryTemplateVersionData(publishedVersion, inquiryTemplate, false));
  };

const VERSION_DIFF_KEYS_TO_INFLECT = [
  'steps',
  'verification-checks',
  'country-codes',
  'field-schema',
  'verification-template-versions',
  'workflow-versions',
  'components',
];

const snakeCaseFromKebabCaseKeysRec = <R>(input: Record<string, unknown>, inflectKeys = false): R =>
  Object.fromEntries(
    Object.entries(input).map(([key, value]) => {
      const formattedKey = inflectKeys ? key.replace(/-/g, '_') : key;
      const formattedValue =
        value?.constructor === Object
          ? snakeCaseFromKebabCaseKeysRec(
              value as Record<string, unknown>,
              VERSION_DIFF_KEYS_TO_INFLECT.includes(key),
            )
          : value;

      return [formattedKey, formattedValue];
    }),
  ) as R;

export const loadVersionDiff =
  (inquiryTemplateId: string): DashboardThunkAction =>
  async (dispatch, getState) => {
    const state = getState();
    const locale = state.inquiryTemplate?.locale ?? 'en-US';
    const {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'inquiry-template-version-diff': inquiryTemplateVersionDiff,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'base-inquiry-template-version-data': baseInquiryTemplateVersionData,
    } = (
      await SessionClientDashboard.getResource<
        InquiryTemplate,
        {
          'inquiry-template-version-diff': InquiryTemplateVersionDiff;
          'base-inquiry-template-version-data': BaseInquiryTemplateVersionData;
        }
      >(
        ApiEndpoint.InquiryTemplate,
        `${inquiryTemplateId}/version-diff?${qs.stringify({ locale })}`,
      )
    ).meta;

    dispatch(
      setVersionDiff({
        versionDiff: {
          inquiryTemplateVersionDiff: snakeCaseFromKebabCaseKeysRec(inquiryTemplateVersionDiff),
          baseInquiryTemplateVersionData: {
            fieldSchema: baseInquiryTemplateVersionData['field-schema'],
            workflowVersions: keyBy('id', baseInquiryTemplateVersionData['workflow-versions'].data),
            steps: baseInquiryTemplateVersionData.steps,
            stepsPreview: baseInquiryTemplateVersionData['steps-preview'],
          },
        },
      }),
    );
  };

/**
 * Screen editor loaders
 */
export const snakeCaseFromKebabCaseKeys = <R>(input: Record<string, unknown>): R =>
  Object.fromEntries(Object.entries(input).map(([key, value]) => [snakeCase(key), value])) as R;

export const loadStepLocalizedStrings =
  (
    inquiryTemplateVersionId: string,
    stepNames: Array<string>,
    unfilteredLocales: Array<InquiryIso6391LanguageCode>,
  ): DashboardThunkAction =>
  async (dispatch, getState) => {
    const {
      inquiryTemplate: { stepLocalizedStrings, inquiryTemplateTreeData, locale: currentLocale },
      form: {
        formInquiryTemplateEditor: {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          values: { 'enabled-locales': unfilteredEnabledLocales },
        },
      },
    } = getState();

    const treeDataSteps = inquiryTemplateTreeData?.inquiryTemplateVersion?.attributes?.steps?.map(
      ({ name }) => name,
    );

    const enabledLocales: Array<InquiryIso6391LanguageCode> = (
      unfilteredLocales ?? unfilteredEnabledLocales
    ).filter((enabledLocale: InquiryIso6391LanguageCode) => enabledLocale !== currentLocale);

    const isSavedStep = (stepName: string) =>
      typeof treeDataSteps?.find((name) => name === stepName) !== 'undefined';

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const { stepNamesToFetch, localesToFetch } = (stepNames ?? treeDataSteps).reduce(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (acc, stepName) => {
        if (!isSavedStep(stepName)) return acc;

        const hyphenStepName = stepName.replace(/_/g, '-');

        if (!stepLocalizedStrings?.[hyphenStepName]) {
          return {
            localesToFetch: [...new Set([...acc.localesToFetch, ...enabledLocales])],
            stepNamesToFetch: [...new Set([...acc.stepNamesToFetch, stepName])],
          };
        }

        const missingLocales = enabledLocales.filter(
          (locale) => !stepLocalizedStrings[hyphenStepName]?.[locale],
        );

        return {
          localesToFetch: [...new Set([...acc.localesToFetch, ...missingLocales])],
          stepNamesToFetch:
            missingLocales.length > 0
              ? [...new Set([...acc.stepNamesToFetch, stepName])]
              : acc.stepNamesToFetch,
        };
      },
      {
        localesToFetch: [],
        stepNamesToFetch: [],
      },
    );

    const shouldFetch =
      unfilteredLocales?.length === 1 && unfilteredLocales[0] === currentLocale
        ? stepNamesToFetch.length > 1
        : stepNamesToFetch.length > 0;

    if (shouldFetch) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const { 'step-localized-translations': result } = (
        await SessionClientDashboard.getResource<
          InquiryTemplateVersion,
          { 'step-localized-translations': StepLocalizedStrings }
        >(
          ApiEndpoint.InquiryTemplateVersion,
          `${inquiryTemplateVersionId}/step-localized-translations`,
          {
            filter: {
              locales: localesToFetch,
              'step-names': stepNamesToFetch,
            },
          },
        )
      ).meta;

      dispatch(setStepLocalizedStrings({ stepLocalizedStrings: result }));
    }
  };

export const uploadAssets =
  (files: AssetUploadData[]): DashboardThunkAction =>
  async (dispatch, getState) => {
    const { inquiryTemplateVersion, schema } = getState().inquiryTemplate.inquiryTemplateTreeData;
    const {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      meta: { assets, 'lock-version': lockVersion },
    } = await SessionClientDashboard.updateResourceAction<
      InquiryTemplateVersion,
      ApiPayload<{ assets: AssetUploadData[] }, ApiModel.InquiryTemplateVersion>,
      { assets: UploadedAsset[]; 'lock-version': number }
    >(
      {
        id: inquiryTemplateVersion.id,
        type: ApiModel.InquiryTemplateVersion,
        attributes: {
          assets: files,
        },
      },
      'upload-assets',
    );
    // updating the lock version in redux is needed here in order to avoid a StaleObjectError
    // because assets currently live on the ITV, so uploading new ones increment the lock version
    const updatedVersion = produce(inquiryTemplateVersion, (draftVersion) => {
      draftVersion.attributes['lock-version'] = lockVersion;
    });
    const assetsWithAspectRatio = await setAssetAspectRatios(assets);
    dispatch(setInquiryTemplateTreeData({ inquiryTemplateVersion: updatedVersion, schema }));
    dispatch(setAssets({ assets: assetsWithAspectRatio }));
  };

/**
 * Visual version diff loaders
 */
export const loadComparedInquiryTemplateVersion =
  (inquiryTemplateVersionId: string): DashboardThunkAction =>
  async (dispatch) => {
    const [{ data: inquiryTemplateVersion }] = await Promise.all([
      SessionClientDashboard.getResource<InquiryTemplateVersion>(
        ApiEndpoint.InquiryTemplateVersion,
        inquiryTemplateVersionId,
      ),
      dispatch(loadComparedSchema(inquiryTemplateVersionId)),
    ]);
    dispatch(setComparedVersion({ comparedVersion: inquiryTemplateVersion }));
  };

const loadComparedSchema =
  (inquiryTemplateVersionId: string): DashboardThunkAction =>
  async (dispatch) => {
    const comparedSchema = await fetchSchema(inquiryTemplateVersionId);
    dispatch(setComparedSchema({ comparedSchema }));
  };

/**
 * Flow Editor History
 */
export const initializeEditorHistory =
  (formData: InquiryTemplateEditorFormData): DashboardThunkAction =>
  (dispatch) => {
    const items = [{ formData, time: new Date().getTime(), isFinal: true }];
    dispatch(
      setEditorHistory({
        items,
        current: items.at(-1),
        historyIndex: 0,
      }),
    );
  };

export const createUndoCheckpoint = (): DashboardThunkAction => (dispatch, getState) => {
  const state = getState();

  const editorHistory = state.inquiryTemplate.editorHistory;
  if (
    editorHistory.items.length > 0 &&
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    !editorHistory.items.at(editorHistory.historyIndex).isFinal
  ) {
    const newItems = [
      ...editorHistory.items.slice(0, editorHistory.historyIndex),
      {
        ...editorHistory.items.at(editorHistory.historyIndex),
        isFinal: true,
      },
    ];
    dispatch(
      setEditorHistory({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        items: newItems,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        current: newItems.at(-1),
        historyIndex: editorHistory.historyIndex,
      }),
    );
  }
};

export const updateHistory =
  (latestValues: InquiryTemplateEditorFormData): DashboardThunkAction =>
  (dispatch, getState) => {
    const state = getState();
    const editorHistory = state.inquiryTemplate.editorHistory;

    // we should always have at least the original state in history
    const mostRecentHistory = editorHistory.items.at(editorHistory.historyIndex);
    if (mostRecentHistory == null) return;

    const overallDiff: any = diff(mostRecentHistory.formData, latestValues);
    // if this is from an undo this condition will be hit and we don't want to store another copy of the same state
    if (isEmpty(overallDiff)) {
      return;
    }

    let createNewEntry = false;

    // if latest entry is marked as final
    if (mostRecentHistory.isFinal) {
      createNewEntry = true;
      // if latest change was > 500 ms ago
    } else if (new Date().getTime() - mostRecentHistory.time > 500) {
      createNewEntry = true;
    } else {
      // latest change involves adding/deleting a key
      const mostRecentChange: any = detailedDiff(mostRecentHistory.formData, latestValues);
      if (!isEmpty(mostRecentChange.added) || !isEmpty(mostRecentChange.deleted)) {
        createNewEntry = true;
      } else {
        // latest change involves changes to a diffferent tab than the one prior
        // changed keys must lie in mostRecentChange.updated since no keys were added / deleted
        // index length - 2 should always exist if most recent entry is not final, because the base entry is initialized with isFinal: true
        const priorChange = diff(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          editorHistory.items.at(editorHistory.historyIndex - 1).formData,
          mostRecentHistory.formData,
        );
        if (!isEqual(Object.keys(mostRecentChange.updated), Object.keys(priorChange))) {
          createNewEntry = true;
        }
      }
    }

    let newHistory = editorHistory;
    if (createNewEntry) {
      const items = [
        ...editorHistory.items.slice(0, editorHistory.historyIndex),
        { ...mostRecentHistory, isFinal: true },
        { formData: latestValues, time: new Date().getTime() },
      ];
      newHistory = {
        items,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        current: items.at(-1),
        historyIndex: items.length - 1,
      };
    } else {
      const items = [
        ...editorHistory.items.slice(0, editorHistory.historyIndex),
        { formData: latestValues, time: new Date().getTime() },
      ];
      newHistory = {
        items: items,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        current: items.at(-1),
        historyIndex: editorHistory.historyIndex,
      };
    }
    dispatch(setEditorHistory(newHistory));
  };

export const undo =
  (navigate: NavigateFunction, numSteps = 1): DashboardThunkAction =>
  async (dispatch, getState) => {
    const state = getState();

    const hasLocalDraft = state.inquiryTemplate.localDraftPartialInquiryTemplateVersion != null;
    const partialInquiryTemplateVersions = state.inquiryTemplates.partialInquiryTemplateVersions;
    const latestPartialInquiryTemplateVersion =
      partialInquiryTemplateVersions[partialInquiryTemplateVersions.length - 1];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const inquiryTemplateId = state.inquiryTemplate.inquiryTemplate.id;

    const history = state.inquiryTemplate.editorHistory;
    let index = history.historyIndex - numSteps;

    const versionId = latestPartialInquiryTemplateVersion?.id;

    if (index < 1 && hasLocalDraft) {
      // If there is a local draft, an undo on a pristine form removes the local draft - reload with the latest version's data
      await dispatch(loadInquiryTemplateVersion(latestPartialInquiryTemplateVersion.id));
      navigate(getEditorPath(inquiryTemplateId, versionId, 'flow'), { replace: true });
    } else {
      index = Math.max(index, 0);
      const revertToState = history.items[index].formData;

      dispatch(
        setEditorHistory({
          ...history,
          current: history.items.at(index),
          historyIndex: index,
        }),
      );
      dispatch(setFormValues(revertToState));
    }
  };

export const redo =
  (numSteps = 1): DashboardThunkAction =>
  (dispatch, getState) => {
    const state = getState();

    const history = state.inquiryTemplate.editorHistory;
    const index = history.historyIndex + numSteps;

    if (history.items.at(index) != null) {
      const jumpToState = history.items[index].formData;

      dispatch(
        setEditorHistory({
          ...history,
          current: history.items.at(index),
          historyIndex: index,
        }),
      );
      dispatch(setFormValues(jumpToState));
    }
  };

export const fetchScreenDimensionsMap = () => {
  const localStorageScreenDimensionsMap = StorageClient.getLocalItem(
    LocalStorageKey.DashboardITEScreenDimensions,
  );
  return JSON.parse(localStorageScreenDimensionsMap ?? '{}') as ScreenDimensionMap;
};

export const updateScreenDimensionsInLocalStorage = (
  inquiryTemplateId: string,
  screenDimensions: ScreenDimensions,
) => {
  const screenDimensionsMap = fetchScreenDimensionsMap();

  const newScreenDimensionsMap = {
    ...screenDimensionsMap,
    [inquiryTemplateId]: screenDimensions,
  };

  StorageClient.setLocalItem(
    LocalStorageKey.DashboardITEScreenDimensions,
    JSON.stringify(newScreenDimensionsMap),
  );
};

/*
 * Screen Dimension Dispatch Function
 * This dispatch function should be in sync with the dispatch functions in:
 * front-end/dashboard/redux/thunk-actions/inquiry-preview.ts
 * If you change one, please check if the other needs changes as well
 */
export const updateEditorScreenDimensions =
  (screenDimensions: ScreenDimensions): DashboardThunkAction =>
  (dispatch, getState) => {
    const state = getState();

    const inquiryTemplateId = state.inquiryTemplate?.inquiryTemplate?.id;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    updateScreenDimensionsInLocalStorage(inquiryTemplateId, screenDimensions);
    dispatch(setEditorScreenDimensions({ screenDimensions }));
  };

export const updateMaxEditorScreenDimensions =
  (
    screenDimensions: ScreenDimensions,
    maxScreenDimensions: ScreenDimensions,
  ): DashboardThunkAction =>
  (dispatch, getState) => {
    // limits the max screen dimensions to not go below the default screen dimensions of 400 x 650
    const limitedMaxScreenDimensions = clampScreenDimensions(maxScreenDimensions);

    const state = getState();

    const inquiryTemplateId = state.inquiryTemplate?.inquiryTemplate?.id;

    if (
      screenDimensions &&
      screenDimensionsExceedMaxScreenDimensions(screenDimensions, limitedMaxScreenDimensions)
    ) {
      // if the screen dimensions exceed the max screen dimensions, limit the screen dimensions to the new range
      const newScreenDimensions = clampScreenDimensions(
        screenDimensions,
        limitedMaxScreenDimensions,
      );
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      updateScreenDimensionsInLocalStorage(inquiryTemplateId, newScreenDimensions);
      dispatch(setEditorScreenDimensions({ screenDimensions: newScreenDimensions }));
    }

    dispatch(setMaxEditorScreenDimensions({ screenDimensions: limitedMaxScreenDimensions }));
  };

export const clearMaxEditorScreenDimensions = (): DashboardThunkAction => (dispatch) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  dispatch(setMaxEditorScreenDimensions({ screenDimensions: null }));
};
